import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Modal, SafeAreaView } from 'react-native';
import styled from 'styled-components/native';
import Card from '../../../../components/Card';
import LineChart from '../../../../components/charts/LineChart';
import { Caption, H4 } from '../../../../components/Typography';
import Spacer from '../../../../components/Spacer';
import IconButton from '../../../../components/IconButton';
import Button from '../../../../components/Button';
import { Ionicons } from '@expo/vector-icons';
import SelectField, {
  SelectOption
} from '../../../../components/form/SelectField';
import {
  ScreenComponentPlugin,
  ScreenComponentProps
} from '../ScreenComponentPlugin';
import {
  UserAttribute,
  UserAttributeSource
} from '../../../../models/UserAttribute';
import { useFindUserAttributes } from '../../../../hooks/user/useFindUserAttributes';
import ActivityIndicatorScreen from '../../../../components/ActivityIndicatorScreen';
import { useRunQuery } from '../../../../dynamic-forms/hooks/useRunQuery';
import { useQueryStudyParticipantData } from '../../../../hooks/study-participant/useQueryStudyParticipantData';
import {
  enableLandscapeOrientationLock,
  enablePortraitOrientationLock
} from '../../../../tools/DeviceHelper';

const StyledView = styled(Card)`
  position: relative;
  width: 100%;
`;

const StyledTitleView = styled.View`
  width: 100%;
  padding: 16px 40px 0px 20px;
`;

const StyledChartView = styled.View`
  width: 100%;
  height: 300px;
  padding: 0px 12px 100px 12px;
`;

const ChartContainer = styled.View`
  position: relative;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
`;

const StyledHeaderView = styled.View`
  width: 100%;
  min-height: 18px;
  margin-top: 16px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledBackIconButton = styled(IconButton)`
  width: 60px;
  height: 40px;
  position: absolute;
  left: -20px;
  top: -10px;
  z-index: 1;
`;

const StyledModalInfoView = styled.View`
  flex-direction: row;
  justify-content: space-between;
  padding: 8px 12px 0px 12px;
  margin-bottom: 4px;
  width: 100%;
  height: 46px;
`;

const StyledModalInfoTextView = styled.View`
  height: 100%;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const StyledHorizontalView = styled.View`
  flex-direction: row;
  align-items: center;
`;

const StyledSelectFieldView = styled.View`
  min-width: 104px;
  max-width: 200px;
`;

const StyledSelectFieldsContainer = styled.View`
  flex-direction: row;
  gap: 8px;
`;

const StyledCompareButtonContainer = styled.View`
  width: 70%;
  padding: 0px 20px 16px 20px;
  align-self: center;
`;

const NoDataOverlay = styled.View`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding-top: 30%;
  padding-left: 10%;
  padding-right: 10%;
  justify-content: start;
  align-items: center;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: 1;
`;

export interface Meal {
  id?: string;
  studyParticipantId: number;
  intendedStartDate: Date;
  mealItem: string;
  mealTime: string;
  mealType: string;
}

export interface BloodGlucoseComparisonChartCardProps {
  title?: string;
  mealType?: 'breakfast' | 'lunch' | 'dinner' | 'snack';
  preMealMinutes?: number;
  postMealMinutes?: number;
  dataUnit?: 'mg/dL' | 'mmol/L';
  useBaseline?: boolean;
  enableXAxisHover?: boolean;
  wearableDataSources?: WearableDataSourceConfig;
  latestAttributes?: UserAttribute[];
  connectedWearableSources?: UserAttributeSource[];
  embedded?: boolean;
  mealEntityId?: string;
  mealTypeColumnName?: string;
  mealTimeColumnName?: string;
  mealItemColumnName?: string;
}

type DataSourceConfig = {
  dataSource: string;
  category?: string;
};

type WearableDataSourceConfig = {
  healthKit: DataSourceConfig;
  googleFit: DataSourceConfig;
  healthConnect: DataSourceConfig;
  fitbit: DataSourceConfig;
  garmin: DataSourceConfig;
  oura: DataSourceConfig;
};

const BloodGlucoseComparisonChartCard: React.FC<
  ScreenComponentProps<BloodGlucoseComparisonChartCardProps>
> = ({ component, studyParticipant }) => {
  const { findUserAttributes, loadingUserAttributes: loadingAttributes } =
    useFindUserAttributes();

  const {
    title = 'Blood Glucose Comparison',
    mealType = 'breakfast',
    preMealMinutes = 30,
    postMealMinutes = 180,
    dataUnit = 'mg/dL',
    useBaseline = false,
    enableXAxisHover = true,
    wearableDataSources = {
      healthKit: { dataSource: '' },
      googleFit: { dataSource: '' },
      healthConnect: { dataSource: '' },
      fitbit: { dataSource: '' },
      garmin: { dataSource: '' },
      oura: { dataSource: '' }
    },
    mealEntityId,
    mealTypeColumnName = 'meal_type',
    mealTimeColumnName = 'meal_time',
    mealItemColumnName = 'meal_item'
  } = component?.props || {};

  const study = studyParticipant?.study;

  const {
    queryData: mealResults,
    isRunningQuery: loadingMeals,
    runQueryError: mealQueryError,
    runQuery
  } = useRunQuery();

  // Query study participant data for intendedStartDate
  const {
    queryResults: mealDateResults,
    loadingQuery: loadingMealDates,
    loadingQueryError: mealDateQueryError,
    queryData: queryMealDates
  } = useQueryStudyParticipantData(studyParticipant?.id || 0);

  if (mealQueryError) {
    console.error('Meal query error:', mealQueryError);
  }

  if (mealDateQueryError) {
    console.error('Meal date query error:', mealDateQueryError);
  }

  const [userAttributeDataList, setUserAttributeDataList] = useState<
    { attributes: UserAttribute[] }[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const [latestMeal, setLatestMeal] = useState<Meal | null>(null);
  const [allMeals, setAllMeals] = useState<Meal[]>([]);

  // Caches mock data for preview to prevent regeneration on every render
  const [cachedMockData, setCachedMockData] = useState<
    { x: number; y: number }[] | null
  >(null);

  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedOption, setSelectedOption] = useState<number>();

  // Auto-rotate when modal opens/closes
  useEffect(() => {
    if (showModal) {
      // Small delay to allow modal to present
      const timeoutId = setTimeout(() => {
        enableLandscapeOrientationLock();
      }, 100);
      return () => clearTimeout(timeoutId);
    } else {
      enablePortraitOrientationLock();
    }
  }, [showModal]);
  const [selectedPrimaryOption, setSelectedPrimaryOption] = useState<number>();
  const [compareMeal, setCompareMeal] = useState<Meal | null>(null);
  const [primaryMeal, setPrimaryMeal] = useState<Meal | null>(null);
  const [compareDataPoints, setCompareDataPoints] = useState<
    { x: number; y: number; originalTime: Date }[]
  >([]);
  const [primaryDataPoints, setPrimaryDataPoints] = useState<
    { x: number; y: number; originalTime: Date }[]
  >([]);

  // Constants
  const intervalMinutes = 15;
  const maxPreMealMinutes = 180;
  const maxPostMealMinutes = 300;
  const maxNumPoints =
    Math.floor((maxPreMealMinutes + maxPostMealMinutes) / intervalMinutes) + 1;
  const numPoints =
    Math.floor((preMealMinutes + postMealMinutes) / intervalMinutes) + 1;

  const capitalize = (item: string) =>
    item.charAt(0).toUpperCase() + item.slice(1);

  const truncateText = (text: string, maxLength: number = 13): string => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '';

    const timeArray = timeString.split(':');
    if (timeArray.length >= 2) {
      const hours = parseInt(timeArray[0]);
      const minutes = timeArray[1];

      if (isNaN(hours)) return timeString;

      const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      const ampm = hours >= 12 ? 'PM' : 'AM';

      if (minutes === '00') return `${hour12} ${ampm}`;
      return `${hour12}:${minutes} ${ampm}`;
    }

    return timeString;
  };

  const snakeToCamel = (str: string) =>
    str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

  const convertUnit = (value: number, fromUnit: string, toUnit: string) => {
    if (fromUnit === 'mmol/L' && toUnit === 'mg/dL') {
      return value * 18.0182;
    }

    if (fromUnit === 'mg/dL' && toUnit === 'mmol/L') {
      return value / 18.0182;
    }

    return value;
  };

  const generateMockValue = (minutesFromMeal: number) => {
    if (minutesFromMeal < 0) {
      return 6.0 + Math.random();
    } else if (minutesFromMeal < 60) {
      return 6.0 + (minutesFromMeal / 60) * 3.0 + Math.random();
    } else {
      return 9.0 - ((minutesFromMeal - 60) / 120) * 2.0 + Math.random();
    }
  };

  // Generate mock data for chart preview
  const generateMockDataPoints = (dataUnit: string) => {
    const dataPoints: number[] = [];
    for (let i = 0; i < maxNumPoints; i++) {
      const minutesFromMeal = i * intervalMinutes - maxPreMealMinutes;
      let mockValue = generateMockValue(minutesFromMeal);

      const finalValue = convertUnit(mockValue, 'mmol/L', dataUnit);
      dataPoints.push(finalValue);
    }
    return dataPoints;
  };

  const processMealData = useCallback(
    (
      mealResults: any[],
      mealDateResults: any[]
    ): { allMeals: Meal[]; latestMeal: Meal | null } => {
      if (
        !mealResults ||
        !Array.isArray(mealResults) ||
        mealResults.length === 0 ||
        !mealDateResults ||
        !Array.isArray(mealDateResults) ||
        mealDateResults.length === 0 ||
        !Array.isArray(mealDateResults[0]) ||
        mealDateResults[0].length === 0
      ) {
        return { allMeals: [], latestMeal: null };
      }

      // Merge meal data with date data
      const mergedMealData: Meal[] = mealResults
        .map((meal: any) => {
          const matchingDateData = mealDateResults[0].find((dateItem: any) => {
            return dateItem.id === meal.id;
          });

          if (!matchingDateData) {
            console.warn(`No matching date data found for meal ID: ${meal.id}`);
            return null;
          }

          const intendedStartDate = new Date(
            matchingDateData.intendedStartDate
          );

          return {
            id: meal.id?.toString(),
            studyParticipantId: studyParticipant?.id || 0,
            intendedStartDate: intendedStartDate,
            mealItem: meal[snakeToCamel(mealItemColumnName)] || '',
            mealTime: meal[snakeToCamel(mealTimeColumnName)] || '',
            mealType: meal[snakeToCamel(mealTypeColumnName)] || ''
          } as Meal;
        })
        .filter((meal): meal is Meal => meal !== null);

      // Sort by intended start date
      const sortedMeals = mergedMealData.sort(
        (a, b) => a.intendedStartDate.getTime() - b.intendedStartDate.getTime()
      );

      return {
        allMeals: sortedMeals,
        latestMeal:
          sortedMeals.length > 0 ? sortedMeals[sortedMeals.length - 1] : null
      };
    },
    [
      mealTimeColumnName,
      mealItemColumnName,
      mealTypeColumnName,
      studyParticipant?.id
    ]
  );

  const wearableSearchConfigs = useMemo(() => {
    const configs: any[] = [];

    if (wearableDataSources.healthKit.dataSource) {
      configs.push({
        source: UserAttributeSource.HealthKit,
        sourceName: 'Health',
        config: { dataSource: wearableDataSources.healthKit.dataSource }
      });
    }

    if (wearableDataSources.googleFit.dataSource) {
      configs.push({
        source: UserAttributeSource.GoogleFit,
        sourceName: 'Google Fit',
        config: { dataSource: wearableDataSources.googleFit.dataSource }
      });
    }

    if (wearableDataSources.healthConnect?.dataSource) {
      configs.push({
        source: UserAttributeSource.HealthConnect,
        sourceName: 'Health Connect',
        config: { dataSource: wearableDataSources.healthConnect?.dataSource }
      });
    }

    if (wearableDataSources.fitbit.dataSource) {
      configs.push({
        source: UserAttributeSource.Fitbit,
        sourceName: 'Fitbit',
        config: { dataSource: wearableDataSources.fitbit.dataSource }
      });
    }

    if (wearableDataSources.garmin.dataSource) {
      configs.push({
        source: UserAttributeSource.Garmin,
        sourceName: 'Garmin',
        config: { dataSource: wearableDataSources.garmin.dataSource }
      });
    }

    if (wearableDataSources.oura.dataSource) {
      configs.push({
        source: UserAttributeSource.Oura,
        sourceName: 'Oura',
        config: { dataSource: wearableDataSources.oura.dataSource }
      });
    }

    return configs;
  }, [wearableDataSources]);

  // Fetch meal data
  useEffect(() => {
    if (
      studyParticipant?.id &&
      mealEntityId &&
      study?.organizationId &&
      study?.id
    ) {
      const queryExpression = {
        queryType: 'select' as const,
        selects: [],
        mainAlias: {
          type: 'from' as const,
          name: 'meal_data',
          tablePath: `org_${study?.organizationId}_study_${study?.id}`
        },
        wheres: [
          {
            type: 'simple' as const,
            condition: {
              operator: 'equal' as const,
              parameters: [
                'study_participant_id',
                studyParticipant.id.toString()
              ]
            }
          },
          {
            type: 'simple' as const,
            condition: {
              operator: 'equal' as const,
              parameters: [mealTypeColumnName, mealType]
            }
          }
        ],
        orderBy: [
          {
            column: 'modified',
            direction: 'desc' as const
          }
        ]
      };

      // Stagger execution to prevent connection conflicts when multiple instances of component are present
      const delay =
        mealType === 'breakfast' ? 0 : mealType === 'lunch' ? 100 : 200;

      const timeoutId = setTimeout(() => {
        runQuery(queryExpression);

        queryMealDates([
          {
            entityId: mealEntityId,
            columnNames: ['id']
          }
        ]);
      }, delay);

      return () => clearTimeout(timeoutId);
    }
  }, [
    runQuery,
    queryMealDates,
    studyParticipant?.id,
    mealEntityId,
    study,
    mealTypeColumnName,
    mealType
  ]);

  // Calculate date range for wearable data based on available meals
  const wearableDateRange = useMemo(() => {
    if (!allMeals || allMeals.length === 0) {
      return { startDate: undefined, endDate: undefined };
    }

    // Calculate the date range to cover all meals with their pre/post meal windows
    let earliestStart: Date | null = null;
    let latestEnd: Date | null = null;

    for (const meal of allMeals) {
      const [hours, minutes] = meal.mealTime.split(':').map(Number);
      const mealDateTime = new Date(
        meal.intendedStartDate.getFullYear(),
        meal.intendedStartDate.getMonth(),
        meal.intendedStartDate.getDate(),
        hours,
        minutes,
        0,
        0
      );

      const mealStartWindow = new Date(
        mealDateTime.getTime() - preMealMinutes * 60 * 1000
      );
      const mealEndWindow = new Date(
        mealDateTime.getTime() + postMealMinutes * 60 * 1000
      );

      if (!earliestStart || mealStartWindow < earliestStart) {
        earliestStart = mealStartWindow;
      }
      if (!latestEnd || mealEndWindow > latestEnd) {
        latestEnd = mealEndWindow;
      }
    }

    if (earliestStart && latestEnd) {
      // Format dates as YYYY-MM-DD strings
      const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      return {
        startDate: formatDate(earliestStart),
        endDate: formatDate(latestEnd)
      };
    }

    return { startDate: undefined, endDate: undefined };
  }, [allMeals, preMealMinutes, postMealMinutes]);

  // Fetch wearable data
  useEffect(() => {
    if (
      wearableSearchConfigs.length > 0 &&
      studyParticipant?.userId &&
      wearableDateRange.startDate &&
      wearableDateRange.endDate
    ) {
      (async () => {
        setIsLoading(true);
        const userAttributeList: { attributes: UserAttribute[] }[] =
          wearableSearchConfigs.map(() => ({ attributes: [] }));
        for (let i = 0; i < wearableSearchConfigs.length; i++) {
          try {
            const userAttributes =
              (await findUserAttributes(
                studyParticipant.userId,
                wearableSearchConfigs[i]?.config?.dataSource,
                wearableSearchConfigs[i].source,
                undefined,
                undefined,
                wearableDateRange.startDate,
                wearableDateRange.endDate,
                undefined
              )) || [];
            userAttributeList[i].attributes = userAttributes;
          } catch (error) {
            console.error(`Error fetching data for config ${i}:`, error);
            userAttributeList[i].attributes = [];
          }
        }
        setUserAttributeDataList(userAttributeList);
        setIsLoading(false);
      })();
    }
  }, [
    findUserAttributes,
    studyParticipant?.userId,
    wearableSearchConfigs,
    dataUnit,
    wearableDateRange.startDate,
    wearableDateRange.endDate
  ]);

  useEffect(() => {
    if (!studyParticipant?.id && !cachedMockData) {
      const mockPoints = generateMockDataPoints('mmol/L');
      const mockDataPoints = mockPoints.map((value, index) => ({
        x: index,
        y: value
      }));
      setCachedMockData(mockDataPoints);
    } else if (studyParticipant?.id) {
      setCachedMockData(null);
    }
  }, [studyParticipant?.id, cachedMockData, dataUnit]);

  // Process meal data
  useEffect(() => {
    const { allMeals, latestMeal } = processMealData(
      mealResults || [],
      mealDateResults || []
    );

    setAllMeals(allMeals);
    setLatestMeal(latestMeal);
  }, [mealResults, mealDateResults, processMealData]);

  // Generate data points when a comparison meal is selected
  const generateDataPoints = useCallback(
    (meal: Meal) => {
      if (!studyParticipant?.id) return [];

      const primaryWearableData =
        userAttributeDataList.find(
          (data, index) => wearableSearchConfigs[index]?.config?.dataSource
        )?.attributes || [];

      const [hours, minutes] = meal.mealTime.split(':').map(Number);
      const mealDateTime = new Date(
        meal.intendedStartDate.getFullYear(),
        meal.intendedStartDate.getMonth(),
        meal.intendedStartDate.getDate(),
        hours,
        minutes,
        0,
        0
      );

      const relevantWearableReadings = primaryWearableData
        .filter((reading: UserAttribute) => {
          if (!reading.startDate) return false;
          const readingTime = new Date(reading.startDate);
          const timeDiff = readingTime.getTime() - mealDateTime.getTime();
          const minutesDiff = timeDiff / (1000 * 60);
          return (
            minutesDiff >= -preMealMinutes && minutesDiff <= postMealMinutes
          );
        })
        .map((reading: UserAttribute) => ({
          time: new Date(reading.startDate ?? 0),
          value: reading.numberValue || 0,
          unit: reading.unit || 'mg/dL'
        }))
        .sort((a, b) => a.time.getTime() - b.time.getTime());

      const allDataPoints: {
        x: number;
        y: number;
        originalTime: Date;
      }[] = [];

      for (const reading of relevantWearableReadings) {
        const timeDiff = reading.time.getTime() - mealDateTime.getTime();
        const minutesFromMeal = timeDiff / (1000 * 60);

        const convertedValue = convertUnit(
          reading.value,
          reading.unit || 'mg/dL',
          dataUnit
        );

        const xPosition = (minutesFromMeal + preMealMinutes) / intervalMinutes;

        allDataPoints.push({
          x: xPosition,
          y: convertedValue,
          originalTime: reading.time
        });
      }

      return allDataPoints.sort((a, b) => a.x - b.x);
    },
    [
      studyParticipant?.id,
      userAttributeDataList,
      wearableSearchConfigs,
      preMealMinutes,
      postMealMinutes,
      dataUnit,
      intervalMinutes
    ]
  );

  const dataTypeDisplay = useMemo(() => {
    const sources = [
      wearableDataSources.healthKit,
      wearableDataSources.googleFit,
      wearableDataSources.healthConnect,
      wearableDataSources.fitbit,
      wearableDataSources.garmin,
      wearableDataSources.oura
    ];

    for (const source of sources) {
      if (source?.dataSource) {
        return source.dataSource;
      }
    }

    return 'Wearable Data';
  }, [wearableDataSources]);

  const chartDataPoints = useMemo(() => {
    if (!studyParticipant?.id) {
      if (!cachedMockData) {
        return [];
      }

      const startIndex = Math.round(
        (maxPreMealMinutes - preMealMinutes) / intervalMinutes
      );
      const endIndex = startIndex + numPoints;

      return cachedMockData.slice(startIndex, endIndex).map((point, index) => ({
        x: index,
        y: convertUnit(point.y, 'mmol/L', dataUnit)
      }));
    }

    if (!latestMeal) {
      return [];
    }

    return generateDataPoints(latestMeal);
  }, [
    preMealMinutes,
    postMealMinutes,
    dataUnit,
    latestMeal,
    userAttributeDataList,
    wearableSearchConfigs,
    studyParticipant?.id,
    cachedMockData,
    intervalMinutes,
    numPoints
  ]);

  const calculateBaseline = useMemo(() => {
    if (!useBaseline) return 0;

    const zeroIndex = Math.round(preMealMinutes / intervalMinutes);

    // Find data points before the meal
    const baselineData = chartDataPoints.filter(point => point.x < zeroIndex);

    const baseline =
      baselineData.length > 0
        ? baselineData.reduce((sum, point) => sum + point.y, 0) /
          baselineData.length
        : 0;

    return Math.round(baseline);
  }, [useBaseline, preMealMinutes, chartDataPoints]);

  const comparisonOptions = useMemo((): SelectOption[] => {
    if (allMeals.length <= 1) return [];

    const currentPrimaryMeal = primaryMeal || latestMeal;
    return [
      { value: -1, label: 'None' },
      ...allMeals
        .filter(meal => meal.id !== currentPrimaryMeal?.id)
        .map((meal, index) => ({
          value: index,
          label: `${formatTime(
            meal.mealTime
          )} on ${meal.intendedStartDate.toLocaleDateString()}${
            meal.mealItem ? ` • ${truncateText(meal.mealItem)}` : ''
          }`
        }))
    ];
  }, [allMeals, primaryMeal, latestMeal]);

  const primaryOptions = useMemo((): SelectOption[] => {
    if (allMeals.length === 0) return [];

    return allMeals.map((meal, index) => ({
      value: index,
      label: `${formatTime(
        meal.mealTime
      )} on ${meal.intendedStartDate.toLocaleDateString()}${
        meal.mealItem ? ` • ${truncateText(meal.mealItem)}` : ''
      }`
    }));
  }, [allMeals]);

  // Update primary data when selected option changes
  useEffect(() => {
    if (typeof selectedPrimaryOption === 'undefined') {
      setPrimaryMeal(latestMeal);
      if (latestMeal) {
        const primaryPoints = generateDataPoints(latestMeal);
        setPrimaryDataPoints(primaryPoints);
      }
      return;
    }

    const selectedMeal = allMeals[selectedPrimaryOption];

    if (selectedMeal) {
      setPrimaryMeal(selectedMeal);
      const primaryPoints = generateDataPoints(selectedMeal);
      setPrimaryDataPoints(primaryPoints);

      if (primaryPoints.length === 0) {
        console.log(
          'No wearable data available for primary meal:',
          selectedMeal
        );
      }
    }
  }, [selectedPrimaryOption, allMeals, latestMeal, generateDataPoints]);

  // Update comparison data when selected option changes
  useEffect(() => {
    if (typeof selectedOption === 'undefined' || selectedOption === -1) {
      setCompareMeal(null);
      setCompareDataPoints([]);
      return;
    }

    const currentPrimaryMeal = primaryMeal || latestMeal;
    const availableMeals = allMeals.filter(
      meal => meal.id !== currentPrimaryMeal?.id
    );
    const selectedMeal = availableMeals[selectedOption];

    if (selectedMeal) {
      setCompareMeal(selectedMeal);
      const comparisonPoints = generateDataPoints(selectedMeal);
      setCompareDataPoints(comparisonPoints);

      if (comparisonPoints.length === 0) {
        console.log(
          'No wearable data available for comparison meal:',
          selectedMeal
        );
      }
    }
  }, [selectedOption, allMeals, primaryMeal, latestMeal, generateDataPoints]);

  // Generate chart data
  const chartData = useMemo(() => {
    if (chartDataPoints.length === 0) {
      if (!studyParticipant?.id) {
        return [[]];
      }
      return [];
    }

    const filteredPoints = studyParticipant?.id
      ? chartDataPoints.filter(point => point.x >= 0 && point.x <= numPoints)
      : chartDataPoints;

    const series: { x: number; y: number }[] = filteredPoints.map(point => {
      let finalValue = point.y;

      if (useBaseline) {
        finalValue -= calculateBaseline;
      }

      return { x: point.x, y: finalValue };
    });

    const comparisonSeries: { x: number; y: number }[] = [];
    if (compareDataPoints.length > 0) {
      const filteredComparePoints = compareDataPoints.filter(
        point => point.x >= 0 && point.x <= numPoints
      );

      comparisonSeries.push(
        ...filteredComparePoints.map(point => {
          let finalValue = point.y;

          if (useBaseline) {
            finalValue -= calculateBaseline;
          }

          return { x: point.x, y: finalValue };
        })
      );
    }

    return [series, comparisonSeries];
  }, [
    chartDataPoints,
    compareDataPoints,
    useBaseline,
    calculateBaseline,
    numPoints,
    studyParticipant?.id
  ]);

  const expandedChartData = useMemo(() => {
    const currentPrimaryPoints =
      primaryDataPoints.length > 0 ? primaryDataPoints : chartDataPoints;

    if (currentPrimaryPoints.length === 0) {
      if (!studyParticipant?.id) {
        return [[]];
      }
      return [];
    }

    const filteredPoints = studyParticipant?.id
      ? currentPrimaryPoints.filter(
          point => point.x >= 0 && point.x <= numPoints
        )
      : currentPrimaryPoints;

    const series: { x: number; y: number }[] = filteredPoints.map(point => {
      let finalValue = point.y;

      if (useBaseline) {
        finalValue -= calculateBaseline;
      }

      return { x: point.x, y: finalValue };
    });

    const comparisonSeries: { x: number; y: number }[] = [];
    if (compareDataPoints.length > 0) {
      const filteredComparePoints = compareDataPoints.filter(
        point => point.x >= 0 && point.x <= numPoints
      );

      comparisonSeries.push(
        ...filteredComparePoints.map(point => {
          let finalValue = point.y;

          if (useBaseline) {
            finalValue -= calculateBaseline;
          }

          return { x: point.x, y: finalValue };
        })
      );
    }

    return [series, comparisonSeries];
  }, [
    primaryDataPoints,
    chartDataPoints,
    compareDataPoints,
    useBaseline,
    calculateBaseline,
    numPoints,
    studyParticipant?.id
  ]);

  const yAxisRange = useMemo(() => {
    const currentData = showModal ? expandedChartData : chartData;
    if (currentData.length === 0) return { min: undefined, max: undefined };

    let allValues = currentData[0].map(point => point.y);

    if (currentData.length > 1) {
      allValues = allValues.concat(currentData[1].map(point => point.y));
    }

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    const range = maxValue - minValue;

    let interval = range / 4;

    if (interval <= 2) {
      interval = 2;
    } else if (interval <= 5) {
      interval = 5;
    } else if (interval <= 10) {
      interval = 10;
    } else if (interval <= 20) {
      interval = 20;
    } else if (interval <= 30) {
      interval = 30;
    } else {
      interval = Math.ceil(interval / 30) * 30;
    }

    if (useBaseline) {
      const newMin = Math.floor(minValue / interval) * interval;
      const newMax = Math.ceil(maxValue / interval) * interval;
      return {
        min: newMin,
        max: newMax,
        interval,
        dataMin: minValue,
        dataMax: maxValue
      };
    } else {
      const padding = Math.max(interval * 0.05, range * 0.01);
      const paddedMin = minValue - padding;
      const paddedMax = maxValue + padding;

      return {
        min: paddedMin,
        max: paddedMax,
        interval,
        dataMin: minValue,
        dataMax: maxValue
      };
    }
  }, [useBaseline, chartData, expandedChartData, showModal]);

  const yTickValues = useMemo(() => {
    if (!yAxisRange.min || !yAxisRange.max || !yAxisRange.interval)
      return undefined;

    const dataMin = (yAxisRange as any).dataMin ?? yAxisRange.min;
    const dataMax = (yAxisRange as any).dataMax ?? yAxisRange.max;
    const dataRange = dataMax - dataMin;

    const numTicks = 5;
    const calculatedInterval = dataRange / (numTicks - 1);

    let interval = calculatedInterval;
    if (calculatedInterval > 0) {
      const magnitude = Math.pow(
        10,
        Math.floor(Math.log10(calculatedInterval))
      );
      const normalized = calculatedInterval / magnitude;

      if (normalized <= 1) {
        interval = magnitude;
      } else if (normalized <= 2) {
        interval = 2 * magnitude;
      } else if (normalized <= 5) {
        interval = 5 * magnitude;
      } else {
        interval = 10 * magnitude;
      }
    }

    const numTicksDesired = 5;
    const numIntervalsBetweenTicks = numTicksDesired - 1;
    const intervalBetweenTicks = dataRange / numIntervalsBetweenTicks;

    const ticks = [];
    for (let i = 0; i < numTicksDesired; i++) {
      const value = dataMin + i * intervalBetweenTicks;
      const normalizedValue = (value - dataMin) / dataRange;
      ticks.push(normalizedValue);
    }

    return ticks;
  }, [yAxisRange]);

  const xTickFormat = (t: number) => {
    const index = Math.max(0, Math.round(t - 1));
    const minutesFromMeal = index * intervalMinutes - preMealMinutes;

    const zeroIndex = Math.round(preMealMinutes / intervalMinutes);

    const totalRange = postMealMinutes + preMealMinutes;
    const tickInterval = totalRange <= 120 ? 30 : totalRange <= 300 ? 60 : 120;

    const tickIndices = [];
    tickIndices.push(zeroIndex);

    for (let min = tickInterval; min <= preMealMinutes; min += tickInterval) {
      const tickIndex = Math.round((preMealMinutes - min) / intervalMinutes);
      if (tickIndex >= 0 && tickIndex < zeroIndex) {
        tickIndices.push(tickIndex);
      }
    }

    for (let min = tickInterval; min <= postMealMinutes; min += tickInterval) {
      const tickIndex = zeroIndex + Math.round(min / intervalMinutes);
      if (tickIndex < numPoints) {
        tickIndices.push(tickIndex);
      }
    }

    if (tickIndices.includes(index)) {
      return minutesFromMeal > 0 ? `+${minutesFromMeal}` : `${minutesFromMeal}`;
    }
    return '';
  };

  const yTickFormat = useMemo(() => {
    if (!yAxisRange.min || !yAxisRange.max) return undefined;

    return (t: number) => {
      const actualValue =
        yAxisRange.min + t * (yAxisRange.max - yAxisRange.min);
      const roundedValue = Math.round(actualValue);

      if (useBaseline) {
        return roundedValue > 0 ? `+${roundedValue}` : `${roundedValue}`;
      } else {
        return roundedValue.toString();
      }
    };
  }, [useBaseline, yAxisRange]);

  const calculateTooltipPosition = useCallback(
    (percentage: number) => {
      if (!yAxisRange.min || !yAxisRange.max) return 0.9;

      const clampedPercentage = Math.max(0, Math.min(1, percentage));

      const actualYPosition =
        yAxisRange.min + clampedPercentage * (yAxisRange.max - yAxisRange.min);

      return actualYPosition;
    },
    [yAxisRange]
  );

  // Calculate tooltip Y position as a percentage of the total range
  const tooltipYPosition = useMemo(() => {
    return calculateTooltipPosition(0.9);
  }, [calculateTooltipPosition]);

  const hasNoData =
    studyParticipant?.id && (!latestMeal || chartDataPoints.length === 0);

  return (
    <StyledView>
      <StyledTitleView>
        <H4>{title}</H4>
        <Spacer size={1} />
        <Caption color='dark'>
          {capitalize(mealType)}
          {latestMeal?.mealItem
            ? ` - ${truncateText(latestMeal.mealItem, 25)} at `
            : ''}
          {latestMeal?.mealTime ? formatTime(latestMeal.mealTime) : ''}
          {latestMeal?.intendedStartDate
            ? ` on ${latestMeal?.intendedStartDate.toLocaleDateString()}`
            : ''}
        </Caption>
        {useBaseline && (
          <Caption color='dark'>
            {`Baseline - ${Math.round(calculateBaseline)} ${dataUnit}`}
          </Caption>
        )}
      </StyledTitleView>

      <StyledChartView>
        <ChartContainer>
          <LineChart
            key={`${dataUnit}-${dataTypeDisplay}-${useBaseline}`}
            dataPoints={chartData.length > 0 ? [chartData[0]] : [[]]}
            pointSize={2}
            xAxisMaxValueLabel={numPoints}
            xAxisLabel={`Minutes from Meal`}
            xAxisLabelStyle={{
              fontSize: '12px',
              fontFamily: 'WorkSans-Medium'
            }}
            yAxisLeftLabel={`${dataTypeDisplay} (${dataUnit})`}
            yAxisLeftOffset={15}
            capitalizeYAxisLeftLabel={false}
            yAxisMaxValueLabel={
              useBaseline
                ? (yAxisRange.max ?? (dataUnit === 'mg/dL' ? 200 : 12))
                : dataUnit === 'mg/dL'
                  ? 200
                  : 12
            }
            chartStartDate={new Date('2024-01-01T00:00:00Z')}
            leftAxisDataUnit={dataUnit}
            enableRightAxis={typeof selectedOption !== 'undefined'}
            rightAxisDataUnit={dataUnit}
            domainYMin={undefined}
            roundValuesLeftAxis={false}
            yRange={
              yAxisRange.min !== undefined && yAxisRange.max !== undefined
                ? [yAxisRange.min, yAxisRange.max]
                : undefined
            }
            yTickValues={yTickValues}
            yTickFormat={yTickFormat}
            xTickFormat={xTickFormat}
            xGridLineColor={'#D8D8DC'}
            studyStartXPoint={preMealMinutes / intervalMinutes}
            showStudyStarted={false}
            showBaseline={useBaseline}
            enableXAxisHover={enableXAxisHover && !!studyParticipant?.id}
            tooltipYPosition={tooltipYPosition}
            showLegend={false}
            legendLabels={[]}
          />
          {hasNoData && (
            <NoDataOverlay>
              <H4 color='dark' style={{ textAlign: 'center' }}>
                {!latestMeal
                  ? 'No meal data available'
                  : 'No wearable data available around meal time'}
              </H4>
            </NoDataOverlay>
          )}
        </ChartContainer>
      </StyledChartView>
      <StyledCompareButtonContainer>
        <Button
          size='small'
          disabled={
            hasNoData || (allMeals.length <= 1 && !!studyParticipant?.id)
          }
          onPress={() => {
            if (latestMeal && allMeals.length > 0) {
              const latestMealIndex = allMeals.findIndex(
                meal => meal.id === latestMeal.id
              );
              if (latestMealIndex !== -1) {
                setSelectedPrimaryOption(latestMealIndex);
              }
            }
            setSelectedOption(undefined);
            setShowModal(true);
          }}
        >
          Compare
        </Button>
      </StyledCompareButtonContainer>
      {showModal && (
        <Modal
          visible={showModal}
          animationType='slide'
          supportedOrientations={[
            'portrait',
            'landscape-left',
            'landscape-right'
          ]}
          onRequestClose={() => {
            setShowModal(false);
          }}
        >
          <SafeAreaView>
            <StyledModalView>
              <StyledHeaderView>
                <StyledBackIconButton
                  onPress={() => {
                    setShowModal(false);
                  }}
                >
                  <Ionicons size={24} name='arrow-back' />
                </StyledBackIconButton>
                <H4 color='dark'>{title}</H4>
              </StyledHeaderView>
              <StyledModalInfoView>
                <StyledModalInfoTextView>
                  <Caption color='dark'>
                    {capitalize(mealType)}
                    {primaryMeal?.mealItem
                      ? ` - ${truncateText(primaryMeal.mealItem)} at `
                      : ''}
                    {primaryMeal?.mealTime
                      ? formatTime(primaryMeal.mealTime)
                      : ''}
                    {primaryMeal?.intendedStartDate
                      ? ` on ${primaryMeal?.intendedStartDate.toLocaleDateString()}`
                      : ''}
                  </Caption>
                  <StyledHorizontalView>
                    {useBaseline && (
                      <Caption color='dark'>
                        {`Baseline - ${Math.round(
                          calculateBaseline
                        )} ${dataUnit}`}
                      </Caption>
                    )}
                  </StyledHorizontalView>
                </StyledModalInfoTextView>
                {(primaryOptions.length > 0 ||
                  comparisonOptions.length > 0) && (
                  <StyledSelectFieldsContainer>
                    {primaryOptions.length > 0 && (
                      <StyledSelectFieldView>
                        <SelectField
                          onChange={option => {
                            setSelectedPrimaryOption(
                              typeof option === 'number'
                                ? option
                                : Number((option as SelectOption).value)
                            );
                          }}
                          variant='chart'
                          placeholder='Primary chart...'
                          value={selectedPrimaryOption}
                          options={primaryOptions}
                        />
                      </StyledSelectFieldView>
                    )}
                    {comparisonOptions.length > 0 && (
                      <StyledSelectFieldView>
                        <SelectField
                          onChange={option => {
                            setSelectedOption(
                              typeof option === 'number'
                                ? option
                                : (option as SelectOption).value !== -1
                                  ? Number((option as SelectOption).value)
                                  : undefined
                            );
                          }}
                          variant='chart'
                          placeholder='Compare with...'
                          value={selectedOption}
                          options={comparisonOptions}
                        />
                      </StyledSelectFieldView>
                    )}
                  </StyledSelectFieldsContainer>
                )}
              </StyledModalInfoView>
              <StyledView>
                <LineChart
                  expanded
                  key={`${dataUnit}-${dataTypeDisplay}-${useBaseline}-expanded`}
                  dataPoints={expandedChartData}
                  pointSize={2}
                  xAxisMaxValueLabel={numPoints}
                  xAxisLabel={`Minutes from Meal`}
                  xAxisLabelStyle={{
                    fontSize: '12px',
                    fontFamily: 'WorkSans-Medium'
                  }}
                  yAxisLeftLabel={`${dataTypeDisplay} (${dataUnit})`}
                  capitalizeYAxisLeftLabel={false}
                  yAxisLeftOffset={35}
                  yAxisMaxValueLabel={
                    yAxisRange.max ?? (dataUnit === 'mg/dL' ? 200 : 12)
                  }
                  domainYMin={
                    yAxisRange.min !== undefined && yAxisRange.max !== undefined
                      ? yAxisRange.min / yAxisRange.max
                      : undefined
                  }
                  showYAxisTicks={true}
                  chartStartDate={new Date('2024-01-01T00:00:00Z')}
                  leftAxisDataUnit={dataUnit}
                  enableRightAxis={typeof selectedOption !== 'undefined'}
                  rightAxisDataUnit={dataUnit}
                  rightAxisYMaxValueLabel={
                    yAxisRange.max ?? (dataUnit === 'mg/dL' ? 200 : 12)
                  }
                  rightAxisDomainYMin={
                    yAxisRange.min !== undefined && yAxisRange.max !== undefined
                      ? yAxisRange.min / yAxisRange.max
                      : undefined
                  }
                  roundValuesLeftAxis={false}
                  roundValuesRightAxis={false}
                  yTickValues={yTickValues}
                  yTickFormat={yTickFormat}
                  rightAxisTickFormat={() => ''}
                  xTickFormat={xTickFormat}
                  xGridLineColor={'#D8D8DC'}
                  rightAxisLineColor={'#f09d80'}
                  studyStartXPoint={preMealMinutes / intervalMinutes}
                  showStudyStarted={false}
                  showBaseline={useBaseline}
                  enableXAxisHover={enableXAxisHover && !!studyParticipant?.id}
                  tooltipYPosition={tooltipYPosition}
                  showLegend={true}
                  legendPosition='below'
                  legendLabels={[
                    primaryMeal
                      ? `${
                          primaryMeal.mealItem
                            ? `${truncateText(primaryMeal.mealItem, 25)}` +
                              ' • '
                            : ''
                        }${
                          primaryMeal.mealTime && primaryMeal.intendedStartDate
                            ? `${formatTime(
                                primaryMeal.mealTime
                              )} on ${primaryMeal.intendedStartDate.toLocaleDateString()}`
                            : ''
                        }`
                      : 'Primary Data',
                    compareMeal
                      ? `${
                          compareMeal.mealItem
                            ? `${truncateText(compareMeal.mealItem, 25)}` +
                              ' • '
                            : ''
                        }${
                          compareMeal.mealTime && compareMeal.intendedStartDate
                            ? `${formatTime(
                                compareMeal.mealTime
                              )} on ${compareMeal.intendedStartDate.toLocaleDateString()}`
                            : ''
                        }`
                      : 'Comparison Data'
                  ]}
                />
              </StyledView>
              {(isLoading ||
                loadingAttributes ||
                loadingMeals ||
                loadingMealDates) && <ActivityIndicatorScreen modal />}
            </StyledModalView>
          </SafeAreaView>
        </Modal>
      )}
      {(isLoading || loadingAttributes || loadingMeals || loadingMealDates) &&
        !showModal && <ActivityIndicatorScreen modal />}
    </StyledView>
  );
};

export const BloodGlucoseComparisonChartCardPlugin: ScreenComponentPlugin<BloodGlucoseComparisonChartCardProps> =
  {
    name: 'Blood Glucose Comparison Chart Card',
    type: 'BloodGlucoseComparisonChartCard',
    categories: ['my-data'],
    component: BloodGlucoseComparisonChartCard
  };
